import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MobileHeader } from './MobileHeader';
import { Trophy, Medal, Clock, Target, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

interface GuessGame {
  id: string;
  title: string;
  description: string;
  correctAnswer?: string; // Legacy field for backward compatibility
  caseSensitive?: boolean; // Legacy field for backward compatibility
  mode: 'multiple' | 'collect';
  state: 'open' | 'active' | 'closed';
  questions?: Array<{
    id: string;
    question: string;
    correctAnswers: string[];
    caseSensitive: boolean;
    order: number;
  }>;
}

interface LeaderboardEntry {
  playerId?: string; // Optional - only included for admin users
  nickname: string;
  attempts: number;
  questionsCompleted: number[]; // Array of completed question indices
  completionPercentage: number;
  allQuestionsComplete: boolean;
  timeSpent: number | null;
  rank: number;
  submissions?: Array<{
    guess: string;
    isCorrect: boolean;
    questionIndex: number;
    timestamp: number;
  }>;
}

function GuessGameLeaderboard() {
  const { gameId } = useParams<{ gameId: string }>();
  const { player, isAuthenticated } = usePlayer();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [game, setGame] = useState<GuessGame | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!gameId) return;

      try {
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
        // Include requesting user email for admin check
        const queryParams = new URLSearchParams();
        if (user?.email) {
          queryParams.append('requestingUserEmail', user.email);
        }
        const response = await fetch(`${serverUrl}/api/guess-games/${gameId}/leaderboard?${queryParams}`);

        if (response.ok) {
          const data = await response.json();
          setGame(data.game);
          setLeaderboard(data.leaderboard);
          console.log('🏆 Fetched leaderboard:', data.leaderboard);
        } else {
          setError('Failed to load leaderboard');
        }
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
        setError('Failed to load leaderboard');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeaderboard();
  }, [gameId, user]);

  const formatTime = (milliseconds: number | null) => {
    if (!milliseconds) return '--';
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Medal className="h-6 w-6 text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-muted-foreground">#{rank}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto">
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6 text-center">
              <p className="text-destructive mb-4">{error || 'Game not found'}</p>
              <Button onClick={() => navigate('/past-games')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Past Games
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const playerEntry = leaderboard.find(entry => entry.playerId === player?.id);

  return (
    <div className="min-h-screen bg-background">
      <MobileHeader
        isAuthenticated={isAuthenticated}
        user={player ? { name: player.nickname, email: '' } : undefined}
        onSignInClick={() => {}}
        onSignOut={() => {}}
        showBackButton={true}
        onBackClick={() => navigate('/past-games')}
        title="Leaderboard"
      />

      <main className="p-4 space-y-6 max-w-2xl mx-auto">
        {/* Game Info */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="h-5 w-5 text-primary" />
              <Badge variant="outline" className="text-xs">
                {game.mode === 'multiple' ? 'Multiple Attempts' : 'One Attempt'}
              </Badge>
            </div>
            <CardTitle className="font-sans font-semibold text-xl">{game.title}</CardTitle>
            <CardDescription className="font-mono">{game.description}</CardDescription>
            <div className="mt-4 p-3 bg-muted rounded-md">
              <p className="text-sm font-medium text-muted-foreground mb-2">Correct Answer{game.questions && game.questions.length > 1 ? 's' : ''}:</p>
              {game.questions && game.questions.length > 0 ? (
                <div className="space-y-3">
                  {game.questions.map((question, index) => (
                    <div key={question.id} className="border-l-2 border-primary pl-3">
                      {game.questions!.length > 1 && (
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Question {index + 1}
                        </p>
                      )}
                      <p className="font-mono text-sm mb-1">{question.question}</p>
                      <div className="flex flex-wrap gap-1">
                        {question.correctAnswers.map((answer, answerIndex) => (
                          <span key={answerIndex} className="font-mono font-semibold text-base bg-primary/10 px-2 py-1 rounded">
                            {answer}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="font-mono font-semibold text-lg">{game.correctAnswer || 'N/A'}</p>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Player's Score Card */}
        {playerEntry && (
          <Card className="border-primary/50 bg-primary/5">
            <CardHeader>
              <CardTitle className="font-sans font-semibold text-sm">Your Result</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getRankIcon(playerEntry.rank)}
                  <div>
                    <p className="font-semibold">{playerEntry.nickname}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Target className="h-3 w-3" />
                      <span>{playerEntry.attempts} {playerEntry.attempts === 1 ? 'attempt' : 'attempts'}</span>
                      {playerEntry.allQuestionsComplete && playerEntry.timeSpent && (
                        <>
                          <Clock className="h-3 w-3 ml-2" />
                          <span>{formatTime(playerEntry.timeSpent)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <Badge variant={playerEntry.allQuestionsComplete ? "default" : "secondary"}>
                  {playerEntry.allQuestionsComplete 
                    ? `✓ Completed (${playerEntry.questionsCompleted.length} questions)` 
                    : `${playerEntry.completionPercentage}% Complete`}
                </Badge>
              </div>
              {playerEntry.submissions && playerEntry.submissions.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Your Answers:</p>
                  <div className="space-y-2">
                    {playerEntry.submissions.map((submission, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {submission.isCorrect ? (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                        )}
                        {game.questions && game.questions.length > 1 && (
                          <span className="text-xs text-muted-foreground">Q{submission.questionIndex + 1}:</span>
                        )}
                        <span className={`font-mono ${submission.isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                          {submission.guess}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="font-sans font-semibold">Final Standings</CardTitle>
            <CardDescription className="font-mono">
              {leaderboard.filter(e => e.allQuestionsComplete).length} completed all questions • {leaderboard.length} total participants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leaderboard.map((entry) => (
                <div
                  key={entry.playerId || entry.nickname}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    entry.playerId === player?.id
                      ? 'bg-primary/10 border-primary/50'
                      : 'bg-muted/30'
                  }`}
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-8 flex justify-center pt-1">
                      {getRankIcon(entry.rank)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${entry.playerId === player?.id ? 'font-semibold' : ''}`}>
                        {entry.nickname}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <div className="flex items-center gap-1">
                          <Target className="h-3 w-3" />
                          <span>{entry.attempts} {entry.attempts === 1 ? 'attempt' : 'attempts'}</span>
                        </div>
                        {entry.allQuestionsComplete && entry.timeSpent && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{formatTime(entry.timeSpent)}</span>
                          </div>
                        )}
                      </div>
                      {/* Show submissions below attempt count */}
                      {entry.submissions && entry.submissions.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {entry.submissions.slice(0, 3).map((submission, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 text-xs">
                              {submission.isCorrect ? (
                                <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />
                              ) : (
                                <XCircle className="h-3 w-3 text-red-600 flex-shrink-0" />
                              )}
                              {game.questions && game.questions.length > 1 && (
                                <span className="text-muted-foreground">Q{submission.questionIndex + 1}:</span>
                              )}
                              <span className={`font-mono truncate ${submission.isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                {submission.guess}
                              </span>
                            </div>
                          ))}
                          {entry.submissions.length > 3 && (
                            <p className="text-xs text-muted-foreground pl-4">
                              +{entry.submissions.length - 3} more
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <Badge variant={entry.allQuestionsComplete ? "default" : "secondary"} className="text-xs flex-shrink-0">
                      {entry.allQuestionsComplete 
                        ? `✓ ${entry.completionPercentage}%` 
                        : `${entry.completionPercentage}%`}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="space-y-2">
          <Button variant="outline" className="w-full" onClick={() => navigate('/past-games')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Past Games
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate('/lobby')}>
            Return to Lobby
          </Button>
        </div>
      </main>
    </div>
  );
}

export default GuessGameLeaderboard;

